import React from 'react';
import { Card, Button, TextInput, Badge, Spinner, useToast } from '@parsina/framework';
import { Coins } from 'lucide-react';

import { C, CT, soft } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { fmtParseh, TOPUP_PACKAGES, RECOMMENDED_PACKAGE } from '../../constants/pricing';
import { PAYMENT_GATEWAY, gatewayLabel } from '../../constants/gateway';
import { packageToman, parseTomanInput, MIN_TOPUP_TOMAN } from './Credits.shared.js';

// شارژ پارسه — pick an amount, go to the bank.
//
// ── The two units, and the silent one ────────────────────────────────────
//
// The PACKAGES are in پارسه; the CUSTOM field is in Toman; and
// `topUpWallet(amount)` takes the amount in THE GATEWAY'S OWN WIRE UNIT, which
// Zarinpal and Sepehr disagree about (Toman vs Rials). Sending a Toman figure to
// Sepehr undercharges by 10× and the payment SUCCEEDS — nothing errors, the
// student is credited, and the difference is only visible in a reconciliation
// nobody runs.
//
// So this card only ever produces a TOMAN figure, and
// src/constants/gateway.js's `toGatewayAmount` — called once, inside
// `useTopUp` — is the single place that arithmetic happens.
//
// ── What this card must not promise ─────────────────────────────────────
//
// It prints what the student is CHARGED, never what they will be credited. The
// wallet's top-up path currently credits 1 Toman = 1 پارسه with no conversion (a
// known server placeholder), so a package priced from `PARSEH_PRICE_TOMAN` would
// credit a thousand times its own label. The balance shown after the callback
// settles is the wallet's own number and is the only one that is true.
//
// ── And it must not open the bank in an iframe ──────────────────────────
//
// A full-page redirect. The banks refuse to render inside a frame, and the
// failure is a BLANK FRAME rather than an error — the student is left staring at
// nothing with the money not taken.

export default function TopUpCard({ topUp }) {
  const { t, formatNumber } = useTranslation();
  const toast = useToast();

  const [selected, setSelected] = React.useState(RECOMMENDED_PACKAGE);
  const [custom, setCustom] = React.useState('');
  const [problem, setProblem] = React.useState('');

  const customToman = parseTomanInput(custom);
  const usingCustom = custom.trim().length > 0;
  const amountToman = usingCustom
    ? (Number.isFinite(customToman) ? customToman : 0)
    : packageToman(selected);

  const start = async () => {
    if (!Number.isFinite(amountToman) || amountToman < MIN_TOPUP_TOMAN) {
      setProblem('min');
      return;
    }
    setProblem('');
    try {
      const result = await topUp.start({
        amountToman,
        description: t('credits.topUp.title'),
      });
      if (!result?.paymentUrl) {
        toast.error(t('credits.topUp.failed'));
        return;
      }
      // FULL PAGE. Never an iframe — see the header.
      window.location.href = result.paymentUrl;
    } catch (err) {
      console.error('[topup] failed:', err?.message);
      toast.error(t('credits.topUp.failed'));
    }
  };

  return (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: soft(C.teal, 0.16),
            color: CT.accentInk,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Coins size={18} aria-hidden />
        </span>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
          {t('credits.topUp.title')}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
        {TOPUP_PACKAGES.map((parseh) => {
          const active = !usingCustom && parseh === selected;
          return (
            <button
              key={parseh}
              type="button"
              onClick={() => {
                setSelected(parseh);
                setCustom('');
                setProblem('');
              }}
              aria-pressed={active}
              style={{
                minHeight: 76,
                padding: '10px 8px',
                border: `1.5px solid ${active ? C.teal : C.border}`,
                borderRadius: 12,
                background: active ? soft(C.teal, 0.1) : C.card,
                cursor: 'pointer',
                fontFamily: 'inherit',
                textAlign: 'center',
                position: 'relative',
              }}
            >
              {parseh === RECOMMENDED_PACKAGE && (
                <Badge
                  variant="success"
                  style={{ position: 'absolute', insetInlineEnd: 6, top: 6, fontSize: 9 }}
                >
                  {t('credits.topUp.recommended')}
                </Badge>
              )}
              <div style={{ fontSize: 18, fontWeight: 800, color: active ? CT.accentInk : C.textPrimary }}>
                {fmtParseh(parseh, formatNumber)}
              </div>
              <div style={{ fontSize: 10.5, color: C.textMuted }}>{t('common.units.parseh')}</div>
              {/* The PRICE, not a promise about the credited balance. */}
              <div style={{ fontSize: 11, fontWeight: 700, color: CT.ink2, marginTop: 4 }}>
                {formatNumber(packageToman(parseh))} {t('common.units.tomanShort')}
              </div>
            </button>
          );
        })}
      </div>

      <TextInput
        label={t('credits.topUp.customLabel')}
        value={custom}
        onChange={(e) => {
          setCustom(e.target.value);
          setProblem('');
        }}
        placeholder={t('credits.topUp.custom')}
        // A digit run, so LTR and a numeric keypad. Persian digits typed here are
        // folded by `parseTomanInput` rather than rejected.
        dir="ltr"
        inputMode="numeric"
        maxLength={12}
        error={problem === 'min' ? t('credits.topUp.min', { amount: MIN_TOPUP_TOMAN }) : ''}
        data-testid="topup-custom"
      />

      <Button onClick={start} disabled={topUp.starting} className="w-full" data-testid="topup-pay">
        {topUp.starting ? <Spinner size={14} color="#fff" /> : <Coins size={16} aria-hidden />}
        {topUp.starting
          ? t('credits.topUp.redirecting')
          : t('credits.topUp.pay', {
              amount: formatNumber(amountToman),
              gateway: gatewayLabel(t, PAYMENT_GATEWAY),
            })}
      </Button>

      <p style={{ margin: 0, fontSize: 11, lineHeight: 1.9, color: C.textMuted }}>
        {t('credits.topUp.note')}
      </p>
    </Card>
  );
}
